import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, MessageCircle, Phone, Youtube } from "lucide-react";
import { FadeUp, WA_LINK } from "@/components/Reveal";

const SOCIALS = [
  { icon: Facebook, href: "https://www.facebook.com/tasteridernc/", label: "Facebook", testid: "footer-social-facebook" },
  { icon: Instagram, href: "https://www.instagram.com/tasteridernc/", label: "Instagram", testid: "footer-social-instagram" },
  { icon: Youtube, href: "https://www.youtube.com/@tasteridecaferestro6192", label: "YouTube", testid: "footer-social-youtube" },
];

const FOOT_LINKS = [
  { to: "/restaurants", label: "Dine-In Restaurants" },
  { to: "/cloud-kitchen", label: "Cloud Kitchen Brands" },
  { to: "/hospital-canteen", label: "Hospital Canteens" },
  { to: "/investors", label: "Invest With Us" },
  { to: "/franchisee", label: "Franchisee Enquiry" },
  { to: "/contact", label: "Contact Us" },
];

export default function ClosingCTA() {
  return (
    <section data-testid="closing-cta" className="relative overflow-hidden bg-obsidian">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 55% 50% at 50% 0%, rgba(224,90,43,0.20) 0%, rgba(11,12,16,0) 70%)" }}
      />

      <div className="relative mx-auto max-w-5xl px-5 pb-20 pt-24 text-center sm:px-8 lg:pt-32">
        <FadeUp>
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-saffron" data-testid="closing-eyebrow">
            The Invitation
          </span>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.08] tracking-tight text-cream sm:text-5xl lg:text-6xl" data-testid="closing-headline">
            The table is set.
            <span className="mt-2 block font-serif italic font-medium text-saffron">Pull up a chair.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
            We're opening our next chapter to a small circle of partners who believe
            Tier-2 India eats first. One message is all it takes.
          </p>
        </FadeUp>
        <FadeUp delay={0.3} className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a
            data-testid="closing-whatsapp-button"
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-full bg-wa px-8 py-4 font-display text-base font-semibold text-obsidian transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(37,211,102,0.4)]"
          >
            <MessageCircle size={19} strokeWidth={2.4} />
            WhatsApp +91 96619 68613
          </a>
          <a
            data-testid="closing-email-button"
            href="mailto:help@tasteriderestro.com"
            className="flex items-center gap-3 rounded-full border border-white/20 px-8 py-4 font-display text-base font-semibold text-cream transition-[border-color,background-color,transform] duration-300 hover:-translate-y-1 hover:border-saffron hover:bg-saffron/10"
          >
            <Mail size={18} className="text-saffron" />
            help@tasteriderestro.com
          </a>
        </FadeUp>
      </div>

      <footer className="relative border-t border-white/10" data-testid="site-footer">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-16 sm:px-8 md:grid-cols-12">
          <div className="md:col-span-5">
            <Link to="/" data-testid="footer-logo">
              <img src="/images/tasteride-logo.png" alt="Taste Ride" className="h-8 w-auto" />
            </Link>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              Hospitality Group · Ranchi
            </p>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
              Restaurants, cloud kitchens and hospital canteens — one food company
              feeding Jharkhand since 2010.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.testid}
                  data-testid={s.testid}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors hover:border-saffron hover:text-saffron"
                >
                  <s.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">Explore</p>
            <ul className="mt-5 space-y-3">
              {FOOT_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    data-testid={`footer-link-${l.to.slice(1)}`}
                    to={l.to}
                    className="text-sm text-slate-300 transition-colors hover:text-saffron"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">Reach Us</p>
            <ul className="mt-5 space-y-3 text-sm text-slate-300">
              <li>
                <a data-testid="footer-phone" href="tel:+919661968613" className="flex items-center gap-2 transition-colors hover:text-saffron">
                  <Phone size={14} className="text-saffron" /> +91 96619 68613
                </a>
              </li>
              <li>
                <a data-testid="footer-email" href="mailto:help@tasteriderestro.com" className="flex items-center gap-2 transition-colors hover:text-saffron">
                  <Mail size={14} className="text-saffron" /> help@tasteriderestro.com
                </a>
              </li>
              <li className="text-slate-400">
                Bariatu Road, Ranchi, Jharkhand 834009
              </li>
              <li>
                <a
                  data-testid="footer-website-link"
                  href="https://tasteriderestro.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 underline decoration-white/20 underline-offset-4 transition-colors hover:text-saffron"
                >
                  tasteriderestro.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-6 sm:px-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              © 2026 Bucks Taste Ride Pvt. Ltd.
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">
              Made with fire in Ranchi
            </p>
          </div>
        </div>
      </footer>
    </section>
  );
}
